<template>
  <!-- Comments plan 2: the comments panel inside the editor's preview. The preview's comment layer shows the pins;
       this panel writes, through the editor's login. -->
  <comments-panel
    :visible="true"
    :rows="rows"
    :language-id="languageId"
    :page-id="pageId"
    :user-id="userId"
    :can-resolve="mayResolve"
    :focus-id="focusId"
    :members="members"
    :stale-ids="staleIds"
    :pending-ids="pendingIds"
    :pending-anchor="pendingAnchor"
    :device="device"
    :capture="capture"
    top-selector="#preview-page .modal-header"
    @hidden="$emit('hidden')"
    @changed="changed"
    @apply="applySuggestion"
    @reject="rejectSuggestion"
    @anchor-used="$emit('anchor-used')"
    @focus-thread="$emit('focus-pin', $event)"
    @pin-ids="(ids) => { pinIds = ids; }"
  />
</template>

<script>
import GetPageComments from '@/graphQlQueries/queries/getPageComments';
import CommentsPanel from '@/components/comments/CommentsPanel.vue';
import SuggestionActions from '@/components/comments/suggestionActions';
import {
  canResolve, languageIdFor, languageHandleFor, openCount, toThreads,
} from '@/utils/comments';
import GetCommentMembers from '@/graphQlQueries/queries/getCommentMembers';
import { pinsFor } from '@/utils/commentBridge';

const POLL_MS = 60000;

export default {
  name: 'PreviewComments',
  components: { CommentsPanel },
  mixins: [SuggestionActions],
  props: {
    pageId: { type: Number, required: true },
    userId: { type: Number, default: null },
    isSysAdmin: { type: Boolean, default: false },
    privileges: { type: Array, default: () => [] },
    translationSetup: { type: Object, default: null },
    // The language the preview shows (the editor's pick); null = main.
    picked: { type: String, default: null },
    pendingAnchor: { type: Object, default: null },
    device: { type: String, default: null },
    // Plan 3c: the writer's setup and screenshot spot for a new thread (from the preview).
    capture: { type: Function, default: null },
    // A thread to show, from a pin click: { id, n } so the same id twice still moves.
    focusRequest: { type: Object, default: null },
    // Plan 4: a thread to show once the comments are there (link from a mail or the inbox).
    startFocus: { type: Number, default: null },
    // The page's account, for the people one may @mention.
    groupId: { type: Number, default: null },
  },
  data: () => ({
    rows: [],
    members: [],
    focusId: null,
    startFocusDone: false,
    // The threads the panel's filters let through (null until it says).
    pinIds: null,
    timer: null,
  }),
  computed: {
    languageId() {
      return languageIdFor(this.translationSetup, this.picked);
    },
    mayResolve() {
      return canResolve({ isSysAdmin: this.isSysAdmin, privileges: this.privileges });
    },
    pins() {
      const all = pinsFor(this.rows, this.languageId);
      return this.pinIds ? all.filter((p) => this.pinIds.includes(p.id)) : all;
    },
    open() {
      return openCount(toThreads(this.rows, this.languageId));
    },
  },
  watch: {
    pins(list) {
      this.$emit('pins', list);
    },
    open(n) {
      this.$emit('count', n);
    },
    focusRequest(req) {
      if (!req) return;
      this.focusId = null;
      this.$nextTick(() => { this.focusId = req.id; });
    },
  },
  mounted() {
    this.load();
    this.loadMembers();
    this.timer = setInterval(this.load, POLL_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    async loadMembers() {
      if (!this.groupId) return;
      try {
        const { data } = await this.$apollo.query({ query: GetCommentMembers, variables: { groupId: this.groupId }, fetchPolicy: 'no-cache' });
        this.members = ((data && data.users) || []).filter((u) => u.full_name).map((u) => ({ user_id: u.user_id, full_name: u.full_name }));
      } catch (e) {
        this.members = [];
      }
    },
    async load() {
      try {
        const { data } = await this.$apollo.query({ query: GetPageComments, variables: { pageId: this.pageId }, fetchPolicy: 'no-cache' });
        this.rows = (data && data.page_comments) || [];
        this.checkSuggestions();
        this.showStartFocus();
      } catch (e) {
        // The list stays as it was; the next poll tries again.
      }
    },
    showStartFocus() {
      if (!this.startFocus || this.startFocusDone) return;
      this.startFocusDone = true;
      // A thread in another language is not in this list: say which language to pick above.
      const row = this.rows.find((r) => r.comment_id === this.startFocus);
      const own = row && row.language_id != null ? row.language_id : null;
      if (row && own !== this.languageId) {
        const lang = (languageHandleFor(this.translationSetup, own) || (this.translationSetup && this.translationSetup.main) || '').toUpperCase();
        this.$bvToast.toast(this.$t('webmag.comments.otherLanguage', { lang }), { variant: 'info', solid: true });
        return;
      }
      this.focusId = this.startFocus;
      // After the pins of these rows went out, so the page knows the pin it should show.
      this.$nextTick(() => this.$emit('focus-pin', this.startFocus));
    },
    // The page's own comments (header count, canvas badges) load again too.
    changed() {
      this.load();
      this.$root.$emit('keditor-comments-changed');
    },
  },
};
</script>
